// ═══════════════════════════════════════════════════════════════
// POLLEN GRID — Card Registry + Configurable Layout
// Path: src/components/dashboard/pollen/PollenGrid.jsx
//
// Orchestrates the pollen cards on the dashboard.
// Reads user's card selection from profiles.dashboard_cards.
// Cards can be closed or added from a registry.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Flower2, Plus, X, ArrowUp, ArrowDown, GripVertical } from "lucide-react";
import { useApp } from "../../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../../constants/appConfig";
import { supabase } from "../../../lib/supabase";
import { debugLog } from "../../../utils/debugLog";
import { PollenRecentGifts } from "./PollenRecentGifts";
import { PollenNewDonors } from "./PollenNewDonors";
import { PollenReactivated } from "./PollenReactivated";
import { PollenFading } from "./PollenFading";
import { PollenRetentionDanger } from "./PollenRetentionDanger";
import { PollenRetention } from "./PollenRetention";
import { EngagementAlerts } from "../EngagementAlerts";
import { PipelinePulse } from "../PipelinePulse";
import { PollenPortfolio } from "./PollenPortfolio";
import { PollenRenewals } from "./PollenRenewals";
import { PollenSYBNT } from "./PollenSYBNT";

// ── Card Registry ────────────────────────────────────────────
// Each entry defines a pollen card type. To add a new card,
// register it here — no other changes needed.
export const POLLEN_CARD_REGISTRY = [
  {
    key: "recent_gifts",
    label: "Recent Gifts",
    description: "Go say thank you",
    component: PollenRecentGifts,
  },
  {
    key: "new_donors",
    label: "New Donors",
    description: "Go welcome them",
    component: PollenNewDonors,
  },
  {
    key: "reactivated",
    label: "Reactivated",
    description: "They came back — respond now",
    component: PollenReactivated,
  },
  {
    key: "pollen_fading",
    label: "LYBNT",
    description: "Last year but not this — go before it's gone",
    component: PollenFading,
  },
  {
    key: "sybnt",
    label: "SYBNT",
    description: "Some year but not this — they might give again",
    component: PollenSYBNT,
  },
  {
    key: "renewals",
    label: "Renewals",
    description: "Anniversary donors — time to renew",
    component: PollenRenewals,
  },
  {
    key: "retention_danger",
    label: "Second Gift Opportunities",
    description: "One-time donors — help them give twice",
    component: PollenRetentionDanger,
  },
  {
    key: "donor_retention",
    label: "Donor Retention",
    description: "How well are we keeping donors?",
    component: PollenRetention,
  },
  {
    key: "engagement_alerts",
    label: "Engagement Alerts",
    description: "Relationships that need attention",
    component: EngagementAlerts,
  },
  {
    key: "pipeline_pulse",
    label: "Pipeline Pulse",
    description: "Cultivation arc health at a glance",
    component: PipelinePulse,
  },
  {
    key: "my_portfolio",
    label: "My Portfolio",
    description: "Who should I connect with today?",
    component: PollenPortfolio,
  },
];

const DEFAULT_CARDS = ["recent_gifts", "new_donors", "reactivated", "pollen_fading", "sybnt", "retention_danger", "donor_retention", "engagement_alerts", "pipeline_pulse"];

export const PollenGrid = () => {
  const {
    palette,
    userProfile,
    currentUser,
    activePollenCardKey,
    setActivePollenCardKey,
  } = useApp();
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [glowKey, setGlowKey] = useState(null);

  // Local state for immediate reactivity; seed from profile on mount/change
  const [localCards, setLocalCards] = useState(null);

  // Sync from profile when it loads or changes externally
  useEffect(() => {
    const saved = userProfile?.dashboard_cards;
    if (Array.isArray(saved) && saved.length > 0) {
      setLocalCards(saved);
    } else {
      setLocalCards(DEFAULT_CARDS);
    }
  }, [userProfile]);

  const enabledCards = localCards || DEFAULT_CARDS;

  // Cards available to add (not currently enabled)
  const availableCards = useMemo(() => {
    return POLLEN_CARD_REGISTRY.filter(
      (card) => !enabledCards.includes(card.key)
    );
  }, [enabledCards]);

  // Persist card selection to profiles table + update local state immediately
  const saveCards = useCallback(
    async (newCards) => {
      setLocalCards(newCards); // Immediate UI update
      if (!currentUser?.id) return;
      try {
        const { error } = await supabase
          .from("profiles")
          .update({ dashboard_cards: newCards })
          .eq("id", currentUser.id);

        if (error) {
          debugLog.error("PollenGrid: failed to save card preferences", error);
        } else {
          debugLog.action("PollenGrid: card preferences saved", { cards: newCards });
          if (userProfile) {
            userProfile.dashboard_cards = newCards;
          }
        }
      } catch (err) {
        debugLog.error("PollenGrid: save error", err);
      }
    },
    [currentUser, userProfile]
  );

  const handleCloseCard = useCallback(
    (cardKey) => {
      const newCards = enabledCards.filter((key) => key !== cardKey);
      saveCards(newCards);
      setGlowKey((prev) => (prev === cardKey ? null : prev));
    },
    [enabledCards, saveCards]
  );

  const handleFocusCard = useCallback((cardKey) => {
    setGlowKey(cardKey);
  }, []);

  const handleAddCard = useCallback(
    (cardKey) => {
      const newCards = [...enabledCards, cardKey];
      saveCards(newCards);
      setShowAddMenu(false);
    },
    [enabledCards, saveCards]
  );

  const handleMoveCard = useCallback(
    (cardKey, direction) => {
      const idx = enabledCards.indexOf(cardKey);
      if (idx < 0) return;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= enabledCards.length) return;
      const newCards = [...enabledCards];
      [newCards[idx], newCards[newIdx]] = [newCards[newIdx], newCards[idx]];
      saveCards(newCards);
    },
    [enabledCards, saveCards]
  );

  // Respond to slash-command activation: auto-add if hidden, scroll, focus.
  // Focus (glowKey) persists so the user can always see which card is current.
  useEffect(() => {
    if (!activePollenCardKey) return;
    const inRegistry = POLLEN_CARD_REGISTRY.some(
      (card) => card.key === activePollenCardKey
    );
    if (!inRegistry) {
      setActivePollenCardKey?.(null);
      return;
    }

    // Auto-add if the card isn't already on the dashboard
    if (!enabledCards.includes(activePollenCardKey)) {
      saveCards([...enabledCards, activePollenCardKey]);
    }

    const scrollTimer = setTimeout(() => {
      const element = document.getElementById(
        `pollen-card-${activePollenCardKey}`
      );
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      setGlowKey(activePollenCardKey);
      setActivePollenCardKey?.(null);
    }, 120);

    return () => clearTimeout(scrollTimer);
  }, [activePollenCardKey, enabledCards, saveCards, setActivePollenCardKey]);

  // Resolve enabled card keys to registry entries
  const visibleCards = useMemo(() => {
    return enabledCards
      .map((key) => POLLEN_CARD_REGISTRY.find((card) => card.key === key))
      .filter(Boolean);
  }, [enabledCards]);

  // No cards enabled — show restore prompt
  if (visibleCards.length === 0) {
    return (
      <div
        style={{
          background: palette.cardBg,
          border: `1px solid ${palette.cardBorder}`,
          borderRadius: 10,
          padding: "20px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
        }}
      >
        <Flower2 size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
        <span
          style={{
            fontSize: FONT_SIZES.sm,
            color: palette.text3,
            fontFamily: FONT_FAMILY,
          }}
        >
          All pollen cards hidden.
        </span>
        <button
          onClick={() => saveCards(DEFAULT_CARDS)}
          style={{
            background: "none",
            border: `1px solid ${palette.cardBorder}`,
            borderRadius: 6,
            padding: "4px 10px",
            fontSize: FONT_SIZES.sm,
            fontWeight: 600,
            color: palette.text1,
            fontFamily: FONT_FAMILY,
            cursor: "pointer",
          }}
        >
          Restore defaults
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Section header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 2px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Flower2 size={ICON_SIZES.sm} style={{ color: "#10b981" }} />
          <span
            style={{
              fontSize: FONT_SIZES.md,
              fontWeight: 700,
              color: palette.text1,
              fontFamily: FONT_FAMILY,
            }}
          >
            Pollen View
          </span>
          <span
            style={{
              fontSize: FONT_SIZES.xs,
              color: palette.text3,
              fontFamily: FONT_FAMILY,
              fontStyle: "italic",
            }}
          >
            — Where should I collect pollen today?
          </span>
        </div>

        {/* Reorder + Add card buttons */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button
            onClick={() => setReordering((prev) => !prev)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: reordering ? `${palette.accent1 || "#3b82f6"}12` : "none",
              border: `1px solid ${reordering ? palette.accent1 || "#3b82f6" : palette.cardBorder}`,
              borderRadius: 6,
              padding: "3px 8px",
              fontSize: FONT_SIZES.xs,
              fontWeight: 600,
              color: reordering ? palette.accent1 || "#3b82f6" : palette.text2,
              fontFamily: FONT_FAMILY,
              cursor: "pointer",
            }}
          >
            <GripVertical size={ICON_SIZES.xs} />
            {reordering ? "Done" : "Reorder"}
          </button>
        </div>
        {availableCards.length > 0 && (
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowAddMenu((prev) => !prev)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                background: "none",
                border: `1px solid ${palette.cardBorder}`,
                borderRadius: 6,
                padding: "3px 8px",
                fontSize: FONT_SIZES.xs,
                fontWeight: 600,
                color: palette.text2,
                fontFamily: FONT_FAMILY,
                cursor: "pointer",
              }}
            >
              <Plus size={ICON_SIZES.xs} />
              Add Card
            </button>

            {/* Add card dropdown */}
            {showAddMenu && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: 4,
                  background: palette.cardBg,
                  border: `1px solid ${palette.cardBorder}`,
                  borderRadius: 8,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  zIndex: 50,
                  minWidth: 200,
                  overflow: "hidden",
                }}
              >
                {availableCards.map((card) => (
                  <div
                    key={card.key}
                    onClick={() => handleAddCard(card.key)}
                    style={{
                      padding: "10px 14px",
                      cursor: "pointer",
                      borderBottom: `1px solid ${palette.cardBorder}`,
                      transition: "background 0.1s",
                    }}
                    onMouseEnter={(event) => {
                      event.currentTarget.style.background = `${palette.accent1}08`;
                    }}
                    onMouseLeave={(event) => {
                      event.currentTarget.style.background = "transparent";
                    }}
                  >
                    <div
                      style={{
                        fontSize: FONT_SIZES.sm,
                        fontWeight: 600,
                        color: palette.text1,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {card.label}
                    </div>
                    <div
                      style={{
                        fontSize: FONT_SIZES.xs,
                        color: palette.text3,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {card.description}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Card grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            visibleCards.length === 1
              ? "1fr"
              : "1fr 1fr",
          gap: 16,
        }}
      >
        {visibleCards.map((card, idx) => {
          const CardComponent = card.component;
          const isGlowing = glowKey === card.key;
          return (
            <div
              key={card.key}
              id={`pollen-card-${card.key}`}
              onClick={() => handleFocusCard(card.key)}
              style={{
                position: "relative",
                borderRadius: 10,
                boxShadow: isGlowing
                  ? `0 0 0 3px ${palette.accent1}, 0 0 24px ${palette.accent1}55`
                  : "none",
                transition: "box-shadow 0.4s ease-out",
                cursor: "default",
              }}
            >
              {reordering && (
                <div
                  style={{
                    position: "absolute",
                    top: 4,
                    left: 4,
                    zIndex: 10,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    background: palette.cardBg,
                    border: `1px solid ${palette.cardBorder}`,
                    borderRadius: 6,
                    padding: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  <button
                    onClick={() => handleMoveCard(card.key, -1)}
                    disabled={idx === 0}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: idx === 0 ? "default" : "pointer",
                      padding: 3,
                      display: "flex",
                      opacity: idx === 0 ? 0.2 : 0.7,
                    }}
                    title="Move up"
                  >
                    <ArrowUp size={ICON_SIZES.sm} style={{ color: palette.text1 }} />
                  </button>
                  <button
                    onClick={() => handleMoveCard(card.key, 1)}
                    disabled={idx === visibleCards.length - 1}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: idx === visibleCards.length - 1 ? "default" : "pointer",
                      padding: 3,
                      display: "flex",
                      opacity: idx === visibleCards.length - 1 ? 0.2 : 0.7,
                    }}
                    title="Move down"
                  >
                    <ArrowDown size={ICON_SIZES.sm} style={{ color: palette.text1 }} />
                  </button>
                </div>
              )}
              <CardComponent
                onClose={() => handleCloseCard(card.key)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
